import { Box } from "@mui/material";
import { Typography } from "@platform/design-system";

import { CARD_SX } from "./constants";
import { fmtNumber } from "./formatters";
import { CardTitle, HelpIcon } from "./primitives";

const KEY_METRICS_HELP_TEXT = {
  rfpCount: "RFPs included in this report period.",
  completedRfpCount:
    "RFPs in this report period that have reached completed status.",
  averageDaysToIgo: "Average days from submitted to in good order.",
  averageProducts: "Average number of products on each RFP.",
  averageCarriersQuoted: "Average number of carriers quoted on each RFP.",
  plansPresented: "Plans shown to clients during this report period.",
  hoursSaved: "Estimated hours saved during this report period.",
};

type KeyMetricsCardProps = {
  rfpCount: number;
  // Omit to hide the Avg. Days to IGO tile; the caller gates it on REMOVE_IGO.
  // A provided-but-null value renders the em dash, as any absent metric does.
  averageDaysToIgo?: number | null;
  completedRfpCount: number;
  averageProducts: number;
  averageCarriersQuoted: number;
  plansPresented: number;
  hoursSaved: number;
};

function KeyMetricsCard({
  rfpCount,
  averageDaysToIgo,
  completedRfpCount,
  averageProducts,
  averageCarriersQuoted,
  plansPresented,
  hoursSaved,
}: KeyMetricsCardProps): React.JSX.Element {
  const metrics = [
    {
      label: "Number of RFPs",
      value: fmtNumber(rfpCount),
      description: KEY_METRICS_HELP_TEXT.rfpCount,
    },
    {
      label: "Number of Completed RFPs",
      value: fmtNumber(completedRfpCount),
      description: KEY_METRICS_HELP_TEXT.completedRfpCount,
    },
    ...(averageDaysToIgo === undefined
      ? []
      : [
          {
            label: "Avg. Days to IGO",
            value: averageDaysToIgo == null ? "—" : String(averageDaysToIgo),
            description: KEY_METRICS_HELP_TEXT.averageDaysToIgo,
          },
        ]),
    {
      label: "Avg. Products",
      value: String(averageProducts),
      description: KEY_METRICS_HELP_TEXT.averageProducts,
    },
    {
      label: "Avg. Carriers",
      value: String(averageCarriersQuoted),
      description: KEY_METRICS_HELP_TEXT.averageCarriersQuoted,
    },
    {
      label: "Plans Presented",
      value: fmtNumber(plansPresented),
      description: KEY_METRICS_HELP_TEXT.plansPresented,
    },
    {
      label: "Hours Saved",
      value: fmtNumber(hoursSaved),
      description: KEY_METRICS_HELP_TEXT.hoursSaved,
    },
  ];

  return (
    <Box
      data-testid="key-metrics-card"
      sx={{
        ...CARD_SX,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardTitle>Key Metrics</CardTitle>
      <Box
        sx={{
          mt: 2,
          flex: 1,
          display: "grid",
          gridTemplateColumns: `repeat(${metrics.length}, 1fr)`,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        {metrics.map((m, i) => (
          <Box
            key={m.label}
            sx={{
              p: 2,
              borderRight: i < metrics.length - 1 ? "1px solid" : "none",
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
              <Typography variant="body-medium" color="textSecondary">
                {m.label}
              </Typography>
              <HelpIcon label={m.label} description={m.description} />
            </Box>
            <Typography sx={{ mt: "auto" }}>
              <strong>{m.value}</strong>
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export { KeyMetricsCard };
export type { KeyMetricsCardProps };
